import { getStorageItem, setStorageItem, WebStorageType } from "../../../../common-deprecated/utils/storageUtils";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import type { CarConfigStep } from "../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import type { CarConfigConfigureSection, CarConfigHighlightSection } from "./types/SectionTypes";
import type { CarConfigConfigureSectionsLbx } from "./types/SectionTypesLbx";

// ----------------------------------------------------------------------
// Helper types and consts
// ----------------------------------------------------------------------
const SESSION_STORAGE_ID = "com.toyota.tme.t1.aphcarconfig";

type CarConfigSessionStorageParams = {
    country: string;
    language: string;
};

type CarConfigSessionStorageItemType = {
    financeQuote?: Record<string, string | null>;
    insuranceQuote?: Record<string, string | null>;
    taxLocationId?: string;
    activeSections?: Record<
        string,
        Partial<{
            [CarConfigStep.Configure]: CarConfigConfigureSection | CarConfigConfigureSectionsLbx;
            [CarConfigStep.Highlights]: CarConfigHighlightSection;
        }>
    >;
};

// ------------------------------------------------------------------------
// Storage fns
// ------------------------------------------------------------------------
export const setCarConfigSessionStorage = (
    params: CarConfigSessionStorageParams,
    storageItems: CarConfigSessionStorageItemType,
): void => {
    setStorageItem({ ...params, storageId: SESSION_STORAGE_ID }, storageItems, WebStorageType.session);
};

export const getCarConfigSessionStorage = (
    params: CarConfigSessionStorageParams,
): CarConfigSessionStorageItemType | null => {
    return getStorageItem({ ...params, storageId: SESSION_STORAGE_ID }, WebStorageType.session);
};

// ------------------------------------------------------------------------
// Tax Location
// ------------------------------------------------------------------------
export const getTaxLocationIdFromStorage = (params: CarConfigSessionStorageParams): string => {
    return getCarConfigSessionStorage(params)?.taxLocationId || "";
};

export const setTaxLocationIdToStorage = (params: CarConfigSessionStorageParams, taxLocationId: string): void => {
    setCarConfigSessionStorage(params, { taxLocationId });
};

// ------------------------------------------------------------------------
// Finance and insurance quotes
// ------------------------------------------------------------------------
export const getInsuranceQuoteIdFromStorage = (
    params: CarConfigSessionStorageParams,
    modelId: string,
): string | null => {
    return getCarConfigSessionStorage(params)?.insuranceQuote?.[modelId] || null;
};

export const setInsuranceQuoteIdToStorage = (
    params: CarConfigSessionStorageParams,
    quoteId: string | null,
    modelId: string,
): void => {
    setCarConfigSessionStorage(params, { insuranceQuote: { [modelId]: quoteId } });
};

export const getFinanceQuoteIdFromStorage = (params: CarConfigSessionStorageParams, modelId: string): string | null => {
    return getCarConfigSessionStorage(params)?.financeQuote?.[modelId] || null;
};

export const setFinanceQuoteIdToStorage = (
    params: CarConfigSessionStorageParams,
    quoteId: string | null,
    modelId: string,
): void => {
    setCarConfigSessionStorage(params, { financeQuote: { [modelId]: quoteId } });
};

// ------------------------------------------------------------------------
// active sections
// ------------------------------------------------------------------------
/**
 * Get active section based on the current step (configure/Highlights)
 */
export const getActiveSectionFromStorage = (
    params: CarConfigSessionStorageParams,
    activeStep: Exclude<CarConfigStep, CarConfigStep.Specs>,
    modelId: string,
): CarConfigConfigureSection | CarConfigConfigureSectionsLbx | CarConfigHighlightSection | undefined => {
    return getCarConfigSessionStorage(params)?.activeSections?.[modelId]?.[activeStep];
};

export const setActiveSectionToStorage = (
    params: CarConfigSessionStorageParams,
    activeSection: unknown,
    activeStep: Exclude<CarConfigStep, CarConfigStep.Specs>,
    modelId: string,
): void => {
    const current = getCarConfigSessionStorage(params)?.activeSections?.[modelId];

    setCarConfigSessionStorage(params, {
        activeSections: {
            [modelId]: {
                ...current,
                [activeStep]: activeSection,
            },
        },
    });
};

// ------------------------------------------------------------------------
// Storage fns
// ------------------------------------------------------------------------
export const getSessionStorageParams = ({
    commonSettings,
}: {
    commonSettings: CommonSettingsType;
}): CarConfigSessionStorageParams => ({
    country: commonSettings.country,
    language: commonSettings.language,
});
